import {
  getMomentDate,
  isToday,
  getDuration
} from '../core/utils/dates'
import api from './api'
import API_URLS from '~/core/api-urls'

const normalizeRecordings = recordings =>
  recordings.reduce((prev, next) => {
    const duration = getDuration(next.duration)

    // eslint-disable-next-line no-param-reassign
    prev[next.id] = {
      ...next,
      duration,
      keyMoments: next.keyMoments.map(moment => ({
        ...moment,
        duration: getDuration(moment.duration),
        isHighlight: true,
        fullUrl: next.url
      })).sort((a, b) => a.startTimestamp - b.startTimestamp)
    }

    return prev
  }, {})

const normalizeParticipants = participants =>
  participants.reduce((prev, next) => {
    prev.push({
      customerId: next.customerId,
      jids: next.jids,
      name: next.participantName,
      speakerTime: next.totalDominantSpeakerTime,
      userId: next.userid
    })
    return prev
  }, [])

const normalizePolls = polls => polls.map(poll => {
  const emptyAnswers = poll.answers.reduce((answers, { key, name }) => {
    answers[key] = {
      voters: [],
      name
    }

    return answers
  }, {})

  const accumulatedAnswers = poll.votes.reduce((answers, { voteAnswers, voter }) => {
    const { username } = voter

    voteAnswers.forEach(({ key, value }) => {
      if (value) {
        answers[key].voters.push(username)
      }
    })

    return answers
  }, emptyAnswers)

  const votersNum = poll.votes.length || 1
  const answers = Object.keys(accumulatedAnswers).reduce((res, key) => {
    const value = accumulatedAnswers[key]
    const newAnswer = {
      ...value,
      key,
      percent: ((value.voters.length / votersNum) * 100).toFixed()
    }
    res.push(newAnswer)

    return res
  }, [])

  return {
    answers,
    creator: poll.createdBy.username,
    id: poll.pollId,
    question: poll.question
  }
})

const normalizeActionItems = actionItems => {
  if (!actionItems) return []

  return actionItems.split('\n').map(s => s.trim())
}

const normalizeRecent = recent => {
  const isSameDay = isToday(recent.startTimestamp)
  const dateShort = getMomentDate(recent.startTimestamp).format('MMM D')
  const date = getMomentDate(recent.startTimestamp).format(
    'dddd, MMMM D, YYYY'
  )
  const start = getMomentDate(recent.startTimestamp).format('LT')
  const end = getMomentDate(recent.endTimestamp).format('LT')

  return {
    timestamp: recent.startTimestamp,
    isSameDay,
    date,
    dateShort,
    conference: recent.conference,
    sessionId: recent.sessionId,
    topic: recent.topic || recent.conference.split('@')[0],
    start: recent.startTimestamp,
    end: recent.endTimestamp,
    startTime: start,
    endTime: end,
    resources: {
      chat: recent.chat,
      participants: normalizeParticipants(recent.participants),
      recordings: normalizeRecordings(recent.recordings)
    },
    url: recent.url,
    jids: recent.jids,
    locationUrl: recent.locationUrl
  }
}

const normalizeRecents = recents =>
  recents.reduce((prev, next) => {
    // eslint-disable-next-line no-param-reassign
    prev[next.sessionId] = normalizeRecent(next)
    return prev
  }, {})

export const fetchRecents = async ({ maxResults, lastSessionId, maxSessionId }) => {
  let result = await api.get(API_URLS.recents({ maxResults, lastSessionId, maxSessionId }))

  if (api.isErrorResponse(result)) {
    result = {
      content: {},
      hasMorePages: false
    }
  } else {
    result.content = normalizeRecents(result.content)
  }

  return result
}

export const fetchSessionChat = async sessionId => {
  let result = await api.get(API_URLS.sessionChat(sessionId))

  if (api.isErrorResponse(result)) {
    result = []
  }

  return result
}

export const fetchSessionAI = async sessionId => {
  let result = await api.get(API_URLS.sessionAI(sessionId))

  if (api.isErrorResponse(result)) {
    result = {
      actionItems: null,
      summary: null
    }
  }

  return {
    actionItems: normalizeActionItems(result.actionItems),
    summary: result.summary
  }
}

export const fetchSessionPolls = async sessionId => {
  let result = await api.get(API_URLS.sessionPolls(sessionId))

  if (api.isErrorResponse(result)) {
    result = []
  } else {
    result = normalizePolls(result)
  }

  return result
}

export const fetchRecent = async sessionId => {
  let result = await api.get(API_URLS.recent(sessionId))
  if (api.isErrorResponse(result)) {
    result = null
  } else {
    result = normalizeRecents([result])
  }

  return result
}

export const fetchRecordingPresignedUrl = url =>
  api.get(url)

export const fetchRecordingShareLink = async recordingId => {
  const result = await api.post(API_URLS.shareRecording(recordingId))
  if (api.isErrorResponse(result)) {
    return null
  }

  return result.url
}

export const fetchSharingStatus = async sessionId =>
  api.get(API_URLS.SHARING_LINK(sessionId))

export const createSharingLink = async sessionId =>
  api.post(API_URLS.SHARING_LINK(sessionId))

export const deleteSharingLink = async sessionId =>
  api.del(API_URLS.SHARING_LINK(sessionId))
